import { Controller, ColorFrom, Loader, IDynamicLoader } from '@tencent/workbench';
import {
    FORM_CONFIGS,
    LINEAR_GRADIENT,
    RADIAL_GRADIENT,
    RECT_GRADIENT,
    SHAPE_GRADIENT,
} from '@framework/workbench/drawer/ui/text/fill/config';
import { Form, ColorOperations, NumberInputFrom, AngleFrom, DragAngleCircleType } from '@tencent/wb-ext-form';
import { FormItemName, IMultiDiffValue, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import { IDirection, IFillFeature } from './fill-interface';
import { ANGLE_MAX } from '../../../common/config';

@Controller({ formItems: FORM_CONFIGS })
export class FontFillController {
    constructor(@Loader(IFillFeature) private loader: IDynamicLoader<IFillFeature>) {}

    private get feature() {
        return this.loader.instance!;
    }

    @Form(FormItemName.FONT_FILL_SELECT, 'content')
    public renderFillContent() {
        return this.feature.renderFillContent();
    }

    // 填充 - 下拉选项
    @Form(FormItemName.FONT_FILL_SELECT, 'options')
    public getOptions() {
        return {
            selectOptions: this.feature.getFillTypes(),
        };
    }

    @Form(FormItemName.FONT_SOLID_FILL_COLOR, 'content')
    public getSolidFillColorOptions() {
        return this.feature.getColorSelectButton();
    }

    // 纯色填充 - 透明度
    @Form(FormItemName.FONT_SOLID_FILL_OPACITY, 'value')
    public getSolidFillOpacity(): number | IMultiDiffValue {
        const solidOpacityObj = { solidOpacity: this.feature.getColorOpacity() };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL, solidOpacityObj);
    }

    @Form(FormItemName.FONT_SOLID_FILL_OPACITY, 'onChange')
    public changeSolidFillOpacity(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { solidOpacity } = this.feature.getValidValue({ solidOpacity: value }, from);
        this.feature.handleSolidOpacityChange(solidOpacity, from, dragEnd);
    }

    // 渐变填充 - 线性|径向
    @Form(FormItemName.FONT_GRADUAL_FILL_LINEAR_GRADIENT, 'value')
    public getGradualFillLinear(): boolean {
        return this.feature.fillGradualValues.curGradualType === LINEAR_GRADIENT;
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_LINEAR_GRADIENT, 'onClick')
    public changeGradualFillLinear() {
        this.feature.handleGradualTypeChange(LINEAR_GRADIENT);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_RADIAL_GRADIENT, 'value')
    public getGradualFillRadial(): boolean {
        return this.feature.fillGradualValues.curGradualType === RADIAL_GRADIENT;
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_RADIAL_GRADIENT, 'onClick')
    public changeGradualFillRadial() {
        this.feature.handleGradualTypeChange(RADIAL_GRADIENT);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_RECT_GRADIENT, 'value')
    public getGradualFillRect(): boolean {
        return this.feature.fillGradualValues.curGradualType === RECT_GRADIENT;
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_RECT_GRADIENT, 'onClick')
    public changeGradualFillRect() {
        this.feature.handleGradualTypeChange(RECT_GRADIENT);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_SHAPE_GRADIENT, 'value')
    public getGradualFillShape(): boolean {
        return this.feature.fillGradualValues.curGradualType === SHAPE_GRADIENT;
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_SHAPE_GRADIENT, 'onClick')
    public changeGradualFillShape() {
        this.feature.handleGradualTypeChange(SHAPE_GRADIENT);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_ANGLE, 'value')
    public getGradualFillAngle(): number | IMultiDiffValue {
        const gradualAngleObj = { gradualAngle: this.feature.fillGradualValues.curAngle! };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL, gradualAngleObj);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_ANGLE, 'onChange')
    public changeGradualFillAngle(value: number, from: AngleFrom, dragState?: DragAngleCircleType) {
        const { gradualAngle } = this.feature.getValidValue({ gradualAngle: value }, from, ANGLE_MAX);
        const dragEnd = !dragState || dragState === DragAngleCircleType.STOP;
        this.feature.handleGradualAngleChange(gradualAngle % 360, from, dragEnd);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_DIRECTION, 'content')
    public getGradualFillDirectContent() {
        return this.feature.renderGradualDirection();
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_BAR, 'content')
    public renderGradualFill() {
        return this.feature.renderGradualFill();
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_OPACITY, 'value')
    public getGradualFillOpacity(): number | IMultiDiffValue {
        const color = this.feature.fillGradualValues.activeColor;
        if (!color) {
            return 0;
        }
        const gradualOpacityObj = { gradualOpacity: alphaToOpacity(color.alpha) };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL, gradualOpacityObj);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_OPACITY, 'onChange')
    public changeGradualFillOpacity(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { gradualOpacity } = this.feature.getValidValue({ gradualOpacity: value }, from);
        this.feature.handleGradualOpacityChange(gradualOpacity, from, dragEnd);
    }

    // 图片填充
    @Form(FormItemName.FONT_IMAGE_FILL_UPLOAD, 'onClick')
    public changeImgUploadValue() {
        this.feature.handleImageBgFromFile();
    }

    @Form(FormItemName.FONT_IMAGE_FILL_CLIPBOARD, 'onClick')
    public changeImgUploadClipBoardValue() {
        this.feature.handleImageBgFromClipBoard();
    }

    @Form(FormItemName.FONT_IMAGE_FILL_STYLE, 'content')
    public renderImgFill() {
        return this.feature.renderImgFill();
    }

    @Form(FormItemName.FONT_IMAGE_FILL_OPACITY, 'value')
    public getImgOpacityValue(): number | IMultiDiffValue {
        const opacityObj = { opacity: alphaToOpacity(this.feature.fillImageValues.alpha) };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL, opacityObj);
    }

    @Form(FormItemName.FONT_IMAGE_FILL_OPACITY, 'onChange')
    public changeImgOpacityValue(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { opacity } = this.feature.getValidValue({ opacity: value }, from);
        this.feature.handleImageOpacityChange(opacity, from, dragEnd);
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_ANGLE, 'shouldDisabled')
    @Form(FormItemName.FONT_GRADUAL_FILL_ANGLE_TEXT, 'shouldDisabled')
    public shouldAngleDisabled(): boolean {
        return this.feature.shouldAngleDisabled();
    }

    @Form(FormItemName.FONT_GRADUAL_FILL_DIRECTION, 'shouldDisabled')
    @Form(FormItemName.FONT_GRADUAL_FILL_DIRECTION_TEXT, 'shouldDisabled')
    public shouldDirectionDisabled(): boolean {
        return this.feature.fillGradualValues.curGradualType === SHAPE_GRADIENT;
    }
}
